import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  CButton,
  CCol,
  CInput,
  CLabel,
  CModal,
  CModalBody,
  CModalHeader,
  CRow,
  CSelect,
} from "@coreui/react";

import { useGetBucket } from "./BucketServices";
import CIcon from "@coreui/icons-react";
import { MdHelp } from "react-icons/md";
import { freeSet } from "@coreui/icons";
import {
  DEFAULT_COLOR_SCALE_DATA,
  DEFAULT_HEAT_WEIGHTS,
  PCT_DIFF_FROM_AVG_SLICE_TEXT,
  PCT_DIFF_FROM_AVG_TIME_TEXT,
  PCT_DIFF_FROM_BENCHMARK_TEXT,
} from "./BucketVisConstants";
import ErrorMessageAlerts from "src/reusable/ErrorMessageAlerts";
// react-color is unmaintained upstream; replace when this view is next reworked.
import { SketchPicker } from "react-color";
import OutsideAlerter from "src/reusable/OutsideAlerter";

const HeatWeightsModal = ({
  value,
  onChange,
  isVisible,
  onClose,
  analytic,
}) => {
  // Modal component for setting the heat calculation weights
  const [sliceWeight, setSliceWeight] = useState(DEFAULT_HEAT_WEIGHTS.slice);
  const [timeWeight, setTimeWeight] = useState(DEFAULT_HEAT_WEIGHTS.time);
  const [benchmarkWeight, setBenchmarkWeight] = useState(
    DEFAULT_HEAT_WEIGHTS.benchmark,
  );

  useEffect(() => {
    // If the value prop changes, reset the weights
    setSliceWeight(value?.slice);
    setTimeWeight(value?.time);
    setBenchmarkWeight(value?.benchmark);
  }, [value]);

  useEffect(() => {
    // Whenever any of the weights change, call the onChange func
    onChange({
      slice: sliceWeight,
      time: timeWeight,
      benchmark: benchmarkWeight,
    });
  }, [sliceWeight, timeWeight, benchmarkWeight]);

  return (
    <CModal show={isVisible} onClose={onClose} style={{ textAlign: "left" }}>
      <CModalHeader>
        <CCol sm="9">
          <h3>Heat Weights</h3>
        </CCol>
        <CCol sm="3" style={{ textAlign: "right" }}>
          <CButton
            id="settings-close-button"
            data-attr="buckets:btn:close-heat-weights"
            color="secondary"
            onClick={onClose}
            size="sm"
          >
            <CIcon name="cil-x" />
          </CButton>
        </CCol>
      </CModalHeader>
      <CModalBody>
        <p>
          Adjust weight of different metrics in heat calculation (color of
          bubbles)
        </p>
        {analytic?.config?.slice_by?.length > 0 && (
          // Only allow the slice by to be set if the analysis includes at least one slice by column
          <CRow>
            <CCol xs="4">
              <b>{PCT_DIFF_FROM_AVG_SLICE_TEXT}</b>
            </CCol>
            <CCol xs="8" style={{ textAlign: "center" }}>
              <input
                style={{ width: "100%" }}
                type="range"
                min="1"
                max="200"
                value={sliceWeight}
                onChange={(e) => {
                  setSliceWeight(e.target.value);
                }}
                id="slice-heat-weight"
              />
              <p>{sliceWeight}%</p>
            </CCol>
          </CRow>
        )}
        {analytic?.config?.time_slice && (
          // Only allow the slice by to be set if the analysis includes a time slice
          <CRow>
            <CCol xs="4">
              <b>{PCT_DIFF_FROM_AVG_TIME_TEXT}</b>
            </CCol>
            <CCol xs="8" style={{ textAlign: "center" }}>
              <input
                style={{ width: "100%" }}
                type="range"
                min="1"
                max="200"
                value={timeWeight}
                onChange={(e) => {
                  setTimeWeight(e.target.value);
                }}
                id="time-heat-weight"
              />
              <p>{timeWeight}%</p>
            </CCol>
          </CRow>
        )}
        {analytic?.config?.benchmark && (
          // Only allow the slice by to be set if the analysis includes a benchmark
          <CRow>
            <CCol xs="4">
              <b>{PCT_DIFF_FROM_BENCHMARK_TEXT}</b>
            </CCol>
            <CCol xs="8" style={{ textAlign: "center" }}>
              <input
                style={{ width: "100%" }}
                type="range"
                min="1"
                max="200"
                value={benchmarkWeight}
                onChange={(e) => {
                  setBenchmarkWeight(e.target.value);
                }}
                id="benchmark-heat-weight"
              />
              <p>{benchmarkWeight}%</p>
            </CCol>
          </CRow>
        )}
      </CModalBody>
    </CModal>
  );
};

HeatWeightsModal.propTypes = {
  value: PropTypes.shape({
    slice: PropTypes.number.isRequired,
    time: PropTypes.number.isRequired,
    benchmark: PropTypes.number.isRequired,
  }),
  onChange: PropTypes.func,
  analytic: PropTypes.object.isRequired,
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

HeatWeightsModal.defaultProps = {
  value: DEFAULT_HEAT_WEIGHTS,
  onChange: () => {},
};

const HeatColorItem = ({ value, onChange }) => {
  // A component for an individual "level" of heat color.
  // Captures the desired threshold as well as the color
  const [color, setColor] = useState([0, 0, 0, 0.8]);
  const [threshold, setThreshold] = useState(0.0);

  const [isColorPickerVisible, setIsColorPickerVisible] = useState(false);

  useEffect(() => {
    // The values for the color thresholds are in the format
    // [0.5, [128, 128, 128, 0.8]]
    // [Thresold, [R, G, B, Opacity]]
    setThreshold(value[0]);
    setColor(value[1]);
  }, [value]);

  useEffect(() => {
    onChange([threshold, color]);
  }, [color, threshold]);

  return (
    <div>
      <div style={{ float: "left", marginTop: "8px" }}>
        Heat Value:&nbsp;&nbsp;
      </div>
      <div style={{ float: "left" }}>
        <CInput
          name="colorThreshold"
          style={{
            maxWidth: "75px",
          }}
          type={"number"}
          value={threshold * 100}
          onChange={(e) => setThreshold(e.target.value / 100)}
          autoComplete="False"
        ></CInput>
      </div>
      <div style={{ float: "left", marginTop: "8px", marginLeft: "10px" }}>
        Color:&nbsp;&nbsp;
      </div>
      <div
        // Box that renders the currently selected color.  If the box is clicked, a color
        // picket is shown.
        style={{
          backgroundColor: `rgb(${color[0]},${color[1]},${color[2]})`,
          border: "1px solid black",
          borderRadius: "5px",
          width: "35px",
          height: "35px",
          cursor: "pointer",
          float: "left",
        }}
        onMouseDown={() => {
          setIsColorPickerVisible(!isColorPickerVisible);
        }}
      ></div>
      <div
        // Div for the color picker.  Float it, set a high z index so it shows up over everything
        // With the modal width, this will render off the side of the modal but that is okay, it looks
        // good
        style={{
          float: "left",
          height: "35px",
          zIndex: "1000",
          marginLeft: "5px",
        }}
      >
        {isColorPickerVisible && (
          <OutsideAlerter
            // This component checks when a click is made anywhere in the DOM outside
            // of the color picker.  This allows a user to click off the color picker
            // and minimize it
            handleClickOutside={() => {
              setIsColorPickerVisible(false);
            }}
          >
            <SketchPicker
              color={`rgb(${color[0]},${color[1]},${color[2]})`}
              onChange={(newColor) => {
                setColor([newColor.rgb.r, newColor.rgb.g, newColor.rgb.b, 0.8]);
              }}
              disableAlpha={true}
            />
          </OutsideAlerter>
        )}
      </div>
      <div style={{ clear: "both" }}></div>
    </div>
  );
};

HeatColorItem.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func,
};

HeatColorItem.defaultProps = {
  value: [0.0, [0, 0, 0, 0.8]],
  onChange: () => {},
};

const HeatColorModal = ({ value, onChange, isVisible, onClose }) => {
  // Modal component for setting the heat calculation weights
  const [colorData, setColorData] = useState(DEFAULT_COLOR_SCALE_DATA);
  useEffect(() => {
    // If the value prop changes, reset the colors
    setColorData(value);
  }, [value]);

  useEffect(() => {
    // Whenever any of the colors change, call the onChange func
    onChange(colorData);
  }, [colorData]);

  return (
    <CModal
      show={isVisible}
      onClose={onClose}
      style={{ textAlign: "left" }}
      size="sm"
    >
      <CModalHeader>
        <CCol sm="9">
          <h3>Heat Colors</h3>
        </CCol>
        <CCol sm="3" style={{ textAlign: "right" }}>
          <CButton
            id="settings-close-button"
            data-attr="buckets:btn:close-heat-colors"
            color="secondary"
            onClick={onClose}
            size="sm"
          >
            <CIcon name="cil-x" />
          </CButton>
        </CCol>
      </CModalHeader>
      <CModalBody>
        <p>Adjust the colors for different heat values.</p>
        <CRow>
          <CCol xs="12">
            {colorData?.map((colorInfo, index) => {
              return (
                <div key={index} style={{ borderTop: "1px solid black" }}>
                  <div
                    key={index}
                    style={{
                      padding: "10px 0px",
                      width: "500px",
                    }}
                  >
                    <HeatColorItem
                      value={colorInfo}
                      onChange={(newColorInfo) => {
                        let newColorData = [...colorData];
                        newColorData[index] = newColorInfo;
                        setColorData(newColorData);
                      }}
                    />
                  </div>
                </div>
              );
            })}
            <div style={{ textAlign: "center", paddingTop: "15px" }}>
              <CButton
                size="sm"
                color="warning"
                data-attr="buckets:btn:reset-heat-colors"
                onClick={() => setColorData(DEFAULT_COLOR_SCALE_DATA)}
              >
                Reset All to Default
              </CButton>
            </div>
          </CCol>
        </CRow>
      </CModalBody>
    </CModal>
  );
};

HeatColorModal.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func,
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

HeatColorModal.defaultProps = {
  value: DEFAULT_COLOR_SCALE_DATA,
  onChange: () => {},
};

const BucketHelpModal = ({ isVisible, onClose }) => {
  return (
    <CModal
      size="lg"
      show={isVisible}
      onClose={onClose}
      style={{ textAlign: "left" }}
    >
      <CModalHeader>
        <CCol sm="9">
          <h3>Bucket Help</h3>
        </CCol>
        <CCol sm="3" style={{ textAlign: "right" }}>
          <CButton
            id="help-close-button"
            data-attr="buckets:btn:close-help"
            color="secondary"
            onClick={onClose}
            size="sm"
          >
            <CIcon name="cil-x" />
          </CButton>
        </CCol>
      </CModalHeader>
      <CModalBody>
        <h4>Select a &quot;Slice&quot; and a &quot;Time Period&quot;</h4>
        <p>
          Depending on the analytic options selected, you will see the option at
          the top of the cluster analytics pane to select a &quot;Slice&quot;
          and a &quot;Time Period&quot;. The selection made for these options
          will filter what data is being displayed in the cluster chart.
        </p>
        <h4>About the Cluster Chart</h4>
        <br />
        <h5>Position of bubbles</h5>
        <p>
          The position of the bubbles is correlated with the
          &quot;Similarity&quot; of the labels. Bubbles that are located close
          together represent very similar concepts, and the associated records
          will usually be similar. The x and y locations have no inherent
          meaning, the algorithm used to plot the bubbles simply tries to put
          similar bubbles close together.
        </p>
        <h5>Size of bubbles</h5>
        <p>
          The size of the bubbles on the cluster chart is based on the number of
          records in the selected Slice and Time Period that have that label.
        </p>
        <h5>Color of bubbles</h5>
        <p>
          The color of bubbles is based on a weighted average of three metrics
          for each bubble:
        </p>
        <ul>
          <li>
            <b>{PCT_DIFF_FROM_AVG_SLICE_TEXT}:</b> The relative difference
            between the selected slice and the average of all other slices
            within the same time period. The equation for this metric is{" "}
            <b>
              <i>
                ((slice value) / (average for other slices in time period))-1
              </i>
            </b>
            . For example, if Unit 1 is the selected slice and has 7.5% of
            records associated with a bubble label in March 2022, and Units 2
            and 3 have 6% of records associated with the same bubble label in
            March 2022, the &quot;{PCT_DIFF_FROM_AVG_SLICE_TEXT}&quot; value for
            Unit 1 is 0.25 or 25% ((7.5 / 6) - 1).
          </li>
          <li>
            <b>{PCT_DIFF_FROM_AVG_TIME_TEXT}:</b> The relative difference
            between the selected time period and the average of all previous
            time periods within the same slice. The equation for this metric is{" "}
            <b>
              <i>
                ((time period value) / (average for previous time period values
                in slice))-1
              </i>
            </b>
            . For example, if Unit 1 is the selected slice and has 10% of
            records associated with a bubble label in March 2022, and Unit 1 had
            14% of records associated with the same bubble label in January 2022
            and 18% or records in February 2022, the &quot;
            {PCT_DIFF_FROM_AVG_TIME_TEXT}&quot; value for March 2022 is -0.375
            or 37.5% ((10 / 15) - 1).
          </li>
          <li>
            <b>{PCT_DIFF_FROM_BENCHMARK_TEXT}</b> The relative difference
            between the selected slice and time period and benchmark value. The
            equation for this metric is{" "}
            <b>
              <i>((value) / (benchmark value))-1</i>
            </b>
            . For example, if Unit 1 is the selected slice and has 6% of records
            associated with a bubble label in March 2022, and the benchmark
            value is 4%, the &quot;{PCT_DIFF_FROM_BENCHMARK_TEXT}
            &quot; value for is 0.5 or 50% ((6 / 4) - 1). <b>Note: </b> relative
            differences from benchmarks are typically larger than slice and time
            comparisons, so the default weighting for this metric is only 25%.
          </li>
        </ul>
        If a metric is unavailable (e.g. no time is selected so a &quot;Relative
        Difference from Average - Time&quot; cannot be calculated), the weighted
        average is based on the other available metrics.
        <br />
        <br />
        The weight of the different metrics can be changed by selected the gear
        icon in the top right of the cluster pane.
        <br />
        <br /> The following scale is used to color the bubbles:
        <table>
          <tbody>
            {DEFAULT_COLOR_SCALE_DATA.map((csd, idx) => {
              const thresh = csd[0];

              const color_scale = csd[1];
              const prefix =
                idx === 0
                  ? "<"
                  : idx === DEFAULT_COLOR_SCALE_DATA.length - 1
                    ? ">"
                    : "";
              return (
                <tr key={idx}>
                  <td style={{ textAlign: "center" }}>
                    <b>
                      {prefix}
                      {thresh}&nbsp;
                    </b>
                  </td>
                  <td
                    style={{
                      backgroundColor: `rgba(${color_scale[0]},${color_scale[1]},${color_scale[2]},${color_scale[3]})`,
                      width: "25px",
                      marginLeft: "10px",
                    }}
                  ></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CModalBody>
    </CModal>
  );
};

BucketHelpModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

const BucketVisOptions = ({
  analytic,
  defaultBucketId,
  statsData,
  onParamsChange,
}) => {
  const { data: defaultBucket } = useGetBucket(defaultBucketId);
  const [selectedCrossSection, setSelectedCrossSection] = useState("");
  const [selectedTime, setSelectedTime] = useState(defaultBucket?.config?.time);
  const [crossSectionOptions, setCrossSectionOptions] = useState([]);
  const [timeOptions, setTimeOptions] = useState([]);
  const [heatWeights, setHeatWeights] = useState(DEFAULT_HEAT_WEIGHTS);
  const [heatColors, setHeatColors] = useState(DEFAULT_COLOR_SCALE_DATA);
  const [isHelpVisible, setIsHelpVisible] = useState(false);
  const [isHeatWeightsModalVisible, setIsHeatWeightsModalVisible] =
    useState(false);
  const [isHeatColorModalVisible, setIsHeatColorModalVisible] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);

  useEffect(() => {
    onParamsChange({
      crossSection: selectedCrossSection,
      time: selectedTime,
      heatWeights,
      heatColors,
    });
  }, [
    selectedCrossSection,
    selectedTime,
    heatWeights,
    heatColors,
    onParamsChange,
    defaultBucket?.config?.time,
  ]);

  useEffect(() => {
    // If a default bucket is provided and it is the same analytic ID, default the cross section
    if (
      defaultBucket &&
      defaultBucket.analytic_fk === analytic.id &&
      (defaultBucket.config?.selected_cross_section ||
        defaultBucket.config?.crossSection)
    ) {
      setSelectedCrossSection(
        defaultBucket.config.selected_cross_section ??
          defaultBucket.config.crossSection,
      );
    }
  }, [analytic, defaultBucket]);

  useEffect(() => {
    // Recalc crossSectionOptions, timeOptions, and errorMessages whenever analytics or statsdata changes
    let newErrorMessages = [];
    let newCrossSectionOptions = new Set();
    let newTimeOptions = new Set();
    if (statsData && analytic) {
      if (analytic.model_label_class.model_output_name !== "WANO_CODE") {
        // TODO: reimplement this check with new statsParams
        newErrorMessages.push(
          "Cluster analytics is currently only supported for Wano Code analysis at this time.",
        );
      }
      if (analytic.config.slice_by.length === 1) {
        if (analytic.config.slice_stats[0].name !== "rel_diff") {
          newErrorMessages.push(
            'Please select "Relative Difference from Average" for the "Analyze Slices by" option.',
          );
        } else {
          // dataset returns in format "ColumnName (slice)"
          // stats params save in format "dataset.ColumnName"
          // this logic creates the dataset column name from the stats params column name
          const sliceColumnName = analytic.config.slice_by[0].name + " (slice)";
          // get all of the unique values in the slice column

          statsData.map((d) => {
            newCrossSectionOptions.add(d[sliceColumnName]);
          });
        }
      } else if (analytic.config.slice_by.length > 1) {
        newErrorMessages.push(
          "Please select a single column to slice the data by, multiple columns provided.",
        );
      }
      if (analytic.config.time_slice) {
        if (analytic.config.time_stats[0].name !== "rel_diff") {
          newErrorMessages.push(
            'Please select "Relative Difference from Average" for the "Analyze Time by" option.',
          );
        } else {
          // dataset returns in format "ColumnName (slice)"
          // stats params save in format "dataset.ColumnName"
          // this logic creates the dataset column name from the stats params column name
          const timeSliceColumnName =
            analytic.config.time_slice.field.name + " (time slice)";
          // get all of the unique values in the slice column

          statsData.map((d) => {
            newTimeOptions.add(d[timeSliceColumnName]);
          });
        }
      }
    } else {
      newErrorMessages.push("No data provided.");
    }
    // convert the options to arrays and sort
    newCrossSectionOptions = Array.from(newCrossSectionOptions);
    newCrossSectionOptions.sort();
    newTimeOptions = Array.from(newTimeOptions);
    newTimeOptions.sort();
    setCrossSectionOptions(newCrossSectionOptions);
    setTimeOptions(newTimeOptions);
    setErrorMessages(newErrorMessages);
  }, [statsData, analytic]);

  useEffect(() => {
    // default to latest time period
    if (timeOptions.length > 0) {
      if (defaultBucket && defaultBucket.config?.time) {
        setSelectedTime(defaultBucket.config.time);
      } else {
        setSelectedTime(timeOptions[timeOptions.length - 1]);
      }
    }
  }, [statsData, timeOptions]);

  useEffect(() => {
    // something default bucket changed
    setSelectedTime(defaultBucket?.config?.time);
  }, [defaultBucket]);

  if (errorMessages.length > 0) {
    return <ErrorMessageAlerts errorMessages={errorMessages} />;
  }

  return (
    <div
      style={{
        margin: "0",
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          display: "flex",
          width: "90%",
          justifyContent: "center",
        }}
      >
        {crossSectionOptions.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              marginBottom: "10px",
            }}
          >
            <div style={{ margin: "5px 10px 0px 5px" }}>
              <CLabel>
                <b>Slice</b>&nbsp;
                <i>({analytic.config.slice_by[0].name})</i>
              </CLabel>
            </div>
            <CSelect
              id="slice-field-select"
              data-attr="buckets:select:slice-filter"
              style={{ width: "250px" }}
              value={selectedCrossSection}
              onChange={(e) => {
                setSelectedCrossSection(e.target.value);
              }}
            >
              <option value="">-</option>
              {crossSectionOptions.map((crossSectionOption, idx) => {
                return (
                  <option key={idx} value={crossSectionOption}>
                    {crossSectionOption}
                  </option>
                );
              })}
            </CSelect>
          </div>
        )}
        {crossSectionOptions.length > 0 &&
          timeOptions.length > 0 && ( // seperator
            <div style={{ width: "35px" }} />
          )}
        {timeOptions.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              marginBottom: "10px",
            }}
          >
            <div style={{ margin: "5px 10px 0px 5px" }}>
              <CLabel>
                <b>Time Period</b>&nbsp;
                <i>({analytic.config.time_slice.field.name})</i>
              </CLabel>
            </div>
            <CSelect
              id="time-select"
              data-attr="buckets:select:time-period-filter"
              style={{ width: "250px" }}
              value={selectedTime}
              onChange={(e) => {
                setSelectedTime(e.target.value);
              }}
            >
              <option value="">-</option>
              {timeOptions.map((timeOption, idx) => {
                return (
                  <option key={idx} value={timeOption}>
                    {timeOption}
                  </option>
                );
              })}
            </CSelect>
          </div>
        )}
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          width: "10%",
          justifyContent: "right",
          paddingBottom: "10px",
        }}
      >
        <div style={{ display: "flex" }}>
          <CButton
            size="sm"
            color="secondary"
            data-attr="buckets:btn:open-heat-weights"
            onClick={() => {
              setIsHeatWeightsModalVisible(true);
            }}
          >
            <CIcon content={freeSet.cilEqualizer} />
          </CButton>
        </div>
        <div style={{ display: "flex", marginLeft: "5px" }}>
          <CButton
            size="sm"
            color="secondary"
            data-attr="buckets:btn:open-heat-colors"
            onClick={() => {
              setIsHeatColorModalVisible(true);
            }}
          >
            <CIcon content={freeSet.cilPaintBucket} />
          </CButton>
        </div>
        <div style={{ display: "flex", marginLeft: "5px" }}>
          <CButton
            size="sm"
            color="info"
            data-attr="buckets:btn:open-help"
            onClick={() => {
              setIsHelpVisible(true);
            }}
          >
            <MdHelp
              style={{
                height: "16px",
                width: "16px",
              }}
            />
          </CButton>
        </div>
        <HeatWeightsModal
          value={heatWeights}
          onChange={setHeatWeights}
          isVisible={isHeatWeightsModalVisible}
          onClose={() => {
            setIsHeatWeightsModalVisible(false);
          }}
          analytic={analytic}
        />
        <HeatColorModal
          value={heatColors}
          onChange={setHeatColors}
          isVisible={isHeatColorModalVisible}
          onClose={() => {
            setIsHeatColorModalVisible(false);
          }}
          analytic={analytic}
        />
        <BucketHelpModal
          isVisible={isHelpVisible}
          onClose={() => {
            setIsHelpVisible(false);
          }}
        />
      </div>
    </div>
  );
};

BucketVisOptions.propTypes = {
  analytic: PropTypes.object.isRequired,
  defaultBucketId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  statsData: PropTypes.array.isRequired,
  onParamsChange: PropTypes.func.isRequired,
};

export default BucketVisOptions;
